import classnames from 'classnames';
import { ComponentPropsWithoutRef, useMemo } from 'react';
import OrderConfirmedIcon from '../../../../modules/mobile/assets/icons/OrderConfirmedIcon';
import { ORDER_FULFILLMENT_STATUSES, ORDER_METHODS } from '../../../../services/exports/Constants';
import { useSelector } from 'react-redux';
import { selectOngoingOrder } from '../../../../store/slices/OrderSlice';
import OrderPreparingIcon from '../../../../modules/mobile/assets/icons/OrderPreparingIcon';
import OrderDeliveredIcon from '../../../../modules/mobile/assets/icons/OrderDeliveredIcon';
import OrderPickedIcon from '../../../../modules/mobile/assets/icons/OrderPickedIcon';
import useScreenType from '../../../../hooks/utility/useScreenType';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import CloseIcon from '../../../../modules/mobile/assets/icons/CloseIcon';
import useAppMethods from '../../../../hooks/utility/useAppMethods';
import { Order } from '../../../../services/exports/Interfaces';

type OrderTrackerProps = ComponentPropsWithoutRef<'section'> & {
  order?: Order;
};

const OrderTracker = ({ className, order, ...rest }: OrderTrackerProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:Orders:Components:OrderTracker' });
  const ongoingOrder = useSelector(selectOngoingOrder) ?? order;
  const { formatTime } = useAppMethods();

  const { isDesktop } = useScreenType();
  const isRejected = ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.REJECTED;
  const isPicked = ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.PICKED;
  const isChecked = (status: ORDER_FULFILLMENT_STATUSES) => {
    if (isRejected) return false;
    if (isPicked) return true;

    const expectedStatuses = [status];
    return ongoingOrder?.fulfillment?.events.some((event) => expectedStatuses.includes(event.status));
  };
  const title = t(`${ongoingOrder?.method}.${ongoingOrder?.fulfillment?.status}.title`);
  const description = t(`${ongoingOrder?.method}.${ongoingOrder?.fulfillment?.status}.description`);
  const timeInterval = useMemo(() => {
    if (ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.REJECTED) {
      return;
    }

    if (!ongoingOrder?.fulfillment?.estimated_pickup_time) {
      return;
    }

    const estimatedPickupTime = moment(ongoingOrder.fulfillment.estimated_pickup_time);
    const min = formatTime(estimatedPickupTime);
    const max = formatTime(estimatedPickupTime.add(10, 'minutes'));
    const date = estimatedPickupTime.isSame(moment(), 'day') ? t('today') : estimatedPickupTime.format('dd DD.MM');

    return `${min} - ${max}, ${date}`;
  }, [ongoingOrder?.fulfillment?.estimated_pickup_time, ongoingOrder?.fulfillment?.status]);
  const titleText =
    ongoingOrder?.method === ORDER_METHODS.PICKUP ? timeInterval : t('estimate', { time: timeInterval });

  const items = [
    {
      icon: isRejected ? <CloseIcon color="white" /> : <OrderConfirmedIcon className="w-4 stroke-white" />,
      status: isRejected ? ORDER_FULFILLMENT_STATUSES.REJECTED : ORDER_FULFILLMENT_STATUSES.ACCEPTED,
    },
    {
      icon: <OrderPreparingIcon className="w-4 stroke-white" />,
      status: ORDER_FULFILLMENT_STATUSES.PREPARING,
    },
    {
      icon:
        ongoingOrder?.method === ORDER_METHODS.DELIVERY ? (
          <OrderDeliveredIcon className="w-4 stroke-white" />
        ) : (
          <OrderPickedIcon className="w-4 stroke-white" />
        ),
      status: ORDER_FULFILLMENT_STATUSES.PICKED,
    },
  ];

  return (
    <section
      className={classnames(
        'flex-col space-y-md justify-between',
        {
          'shadow-[0_-4px_6px_0_rgba(0,0,0,0.1)] border-t rounded-t-lg px-md py-lg': !isDesktop,
          'p-md': isDesktop,
        },
        className,
      )}
      {...rest}
    >
      {timeInterval && (
        <h2
          className={classnames({
            'text-sm font-normal bg-brand-primary/30 rounded-lg px-sm': isDesktop,
            'text-brand-primary text-md font-semibold': !isDesktop,
          })}
        >
          {titleText}
        </h2>
      )}
      {!isDesktop && <p className="!mt-2 text-sm font-medium text-gray-700">{title}</p>}
      <div className={classnames('flex', { 'flex-col items-start': isDesktop })}>
        {items.map((item, index) => (
          <>
            <div className={classnames('flex items-center', { 'flex-col': isDesktop })}>
              <div
                key={item.status}
                className={classnames({ 'flex gap-x-md': isDesktop }, items[index + 1] && 'flex-1')}
              >
                <span
                  className={classnames(
                    'size-[30px] bg-mobile-secondary rounded-full flex items-center justify-center p-2',
                    {
                      '!bg-brand-primary': isChecked(item.status),
                      '!bg-danger': index === 0 && isRejected,
                    },
                  )}
                >
                  {item.icon}
                </span>
                {isDesktop && (
                  <span
                    className={classnames('text-sm my-auto', {
                      '!text-brand-primary': isChecked(items[index + 1]?.status),
                      'text-gray-700': !isChecked(items[index + 1]?.status),
                    })}
                  >
                    {t(`${ongoingOrder?.method}.${item.status}.title`)}
                  </span>
                )}
              </div>
            </div>
            {items[index + 1] && (
              <span
                className={classnames('bg-mobile-secondary', {
                  '!bg-brand-primary': isChecked(items[index + 1].status),
                  'flex-1 h-1 my-auto': !isDesktop,
                  'w-1 h-2 ml-[13px]': isDesktop,
                })}
              />
            )}
          </>
        ))}
      </div>
      {!isDesktop && (
        <p
          className={classnames('text-sm font-medium text-gray-700', {
            '!text-brand-primary': isChecked(ongoingOrder?.fulfillment?.status),
            '!text-danger': isRejected,
          })}
        >
          {description}
        </p>
      )}
    </section>
  );
};

export default OrderTracker;
